import React, { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { FormControl } from '../../../common/FormControl'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { societyAdminCreateSchema } from '../../../../forms/schemas/society_admin_create'
import { useUniqueNonAdminUsers } from '../../../../hooks/useUniqueNonAdminUsers'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { unitQueries } from '../../../../api/units'
import { entranceQueries } from '../../../../api/society-entrances'
import { getUserFullName } from '../../../../helpers/user'
import { unitString } from '../../../../hooks/useUnitsString'

interface CreateUpdateAdminFormProps {
  onError: (error: unknown) => void
  onSubmit: (data: InferType<typeof societyAdminCreateSchema>) => Promise<void>
  onClose: () => void
  loading: boolean
  society: ISocietiesModel
}

export const CreateUpdateAdminForm = ({
  loading,
  onError,
  onSubmit,
  onClose,
  society,
}: CreateUpdateAdminFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const uniqueNonAdminUsers = useUniqueNonAdminUsers(society)
  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society._id)
  )
  const { data: societyEntrences } = useQuery(
    entranceQueries.getEntrancesPerSociety(society._id)
  )

  const userOptions = uniqueNonAdminUsers.map((_user) => {
    const userUnits =
      societyUnits?.filter((unit) =>
        unit.residentsList?.find((resident) => resident.userId === _user._id)
      ) || []
    const _unitString = unitString(
      userUnits,
      [society],
      societyEntrences || [],
      true
    )
    return {
      value: _user._id,
      label: `${getUserFullName(_user)} - ${_unitString}`,
    }
  })

  const getDefaultValues = useCallback((): InferType<
    typeof societyAdminCreateSchema
  > => {
    return {
      userIds: [],
    }
  }, [])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyAdminCreateSchema),
    defaultValues: getDefaultValues(),
  })

  return (
    <>
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FormControl
            label={translate('createUpdateAdminForm.labels.users')}
            name={name}
            error={errors.userIds && errors.userIds.message}
          >
            <SelectDropdown
              options={userOptions}
              value={userOptions.filter((option) =>
                value.includes(option.value)
              )}
              onChange={(options) =>
                onChange(options.map((option) => option.value))
              }
              error={!!errors.userIds}
              isMulti
              menuListHeight={200}
            />
          </FormControl>
        )}
        name="userIds"
      />
      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-3">
        <Button
          label={translate('common.actions.cancel')}
          onClick={onClose}
          variant={ButtonVariant.TEXT}
        />
        <Button
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid || loading}
          label={translate('common.actions.save')}
          type="submit"
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </>
  )
}
